import { useCallback, useRef, useState } from 'react';
import type {
  ServerMessage,
  SnapshotRef,
  SourceFile,
} from 'ts-components/PiChat/protocol';
import {
  selectCanSend as selectChatCanSend,
  selectIsBusy as selectChatIsBusy,
  type PiChatState,
} from 'ts-components/PiChat/reducer';

export interface PushResult {
  action: 'created' | 'updated';
  dashboardId: string;
  dashletId: string;
  url: string;
}

interface BlockVersionsState {
  // pointer moves only on server confirmation (-1 when empty)
  snapshots: SnapshotRef[];
  pointer: number;
  filesBySnapshotId: Record<string, SourceFile[]>;
  restoringSnapshotId: string | null;
  pushResult: PushResult | null;
  dashletId: string | null;
  conversationId: string | null;
}

type BlockVersionsAction =
  | { type: 'connecting' }
  | { type: 'server'; message: ServerMessage }
  | { type: 'restoreRequested'; snapshotId: string }
  | { type: 'restoreAborted' }
  | { type: 'filesLoaded'; snapshotId: string; files: SourceFile[] };

const INITIAL_BLOCK_VERSIONS: BlockVersionsState = {
  snapshots: [],
  pointer: -1,
  filesBySnapshotId: {},
  restoringSnapshotId: null,
  pushResult: null,
  dashletId: null,
  conversationId: null,
};

const RESTORE_ERROR_CODES = new Set<string>([
  'busy',
  'state_unavailable',
  'cancelled',
  'restore_busy',
  'unsupported',
  'unknown_snapshot',
  'restore_failed',
  'prompt_failed',
  'session_create_failed',
  'conversation_invalid',
  'model_unavailable',
]);

export const endsRestore = (message: ServerMessage) =>
  message.type === 'snapshot.restored' ||
  (message.type === 'error' &&
    !message.clientMessageId &&
    RESTORE_ERROR_CODES.has(message.code));

const readyUndoStack = ({
  snapshots = [],
  snapshotPointer,
}: Extract<ServerMessage, { type: 'session.ready' }>): Pick<
  BlockVersionsState,
  'snapshots' | 'pointer'
> => ({ snapshots, pointer: snapshotPointer ?? snapshots.length - 1 });

const reduceServerMessage = (
  state: BlockVersionsState,
  message: ServerMessage
): BlockVersionsState => {
  switch (message.type) {
    case 'session.ready':
      return {
        ...state,
        conversationId: message.conversationId,
        dashletId: message.context.dashletId ?? state.dashletId,
        restoringSnapshotId: null,
        ...readyUndoStack(message),
      };
    case 'push.result': {
      const pushed: BlockVersionsState = {
        ...state,
        pushResult: {
          action: message.action,
          dashboardId: message.dashboardId,
          dashletId: message.dashletId,
          url: message.url,
        },
        conversationId: message.conversationId ?? state.conversationId,
        dashletId: message.dashletId,
      };
      const snapshots = [
        ...state.snapshots.slice(0, state.pointer + 1),
        message.snapshot,
      ];
      return { ...pushed, snapshots, pointer: snapshots.length - 1 };
    }
    case 'snapshot.restored': {
      const index = state.snapshots.findIndex(
        (snapshot) => snapshot.id === message.snapshotId
      );
      return {
        ...state,
        restoringSnapshotId: null,
        pushResult: { ...message.push },
        ...(index === -1 ? {} : { pointer: index }),
      };
    }
    case 'error':
      return state.restoringSnapshotId && endsRestore(message)
        ? { ...state, restoringSnapshotId: null }
        : state;
    default:
      return state;
  }
};

const blockVersionsReducer = (
  state: BlockVersionsState,
  action: BlockVersionsAction
): BlockVersionsState => {
  switch (action.type) {
    case 'connecting':
      return INITIAL_BLOCK_VERSIONS;
    case 'server':
      return reduceServerMessage(state, action.message);
    case 'restoreRequested':
      return { ...state, restoringSnapshotId: action.snapshotId };
    case 'restoreAborted':
      return { ...state, restoringSnapshotId: null };
    case 'filesLoaded':
      return {
        ...state,
        filesBySnapshotId: {
          ...state.filesBySnapshotId,
          [action.snapshotId]: action.files,
        },
      };
    default:
      return state;
  }
};

export const useBlockVersions = () => {
  const ref = useRef(INITIAL_BLOCK_VERSIONS);
  const [state, setState] = useState(INITIAL_BLOCK_VERSIONS);
  const dispatch = useCallback((action: BlockVersionsAction) => {
    const before = ref.current;
    ref.current = blockVersionsReducer(before, action);
    setState(ref.current);
    return before;
  }, []);
  return [state, dispatch, ref] as const;
};

type PanelState = Pick<
  PiChatState,
  'connectionState' | 'isStreaming' | 'turnActive'
> &
  Pick<BlockVersionsState, 'restoringSnapshotId' | 'snapshots' | 'pointer'>;

export const selectIsRestoring = (
  state: Pick<PanelState, 'connectionState' | 'restoringSnapshotId'>
) => state.connectionState === 'ready' && state.restoringSnapshotId !== null;

export const selectCurrentSnapshot = (
  state: Pick<BlockVersionsState, 'snapshots' | 'pointer'>
): SnapshotRef | null => state.snapshots[state.pointer] ?? null;

export const selectCurrentFiles = (
  state: Pick<BlockVersionsState, 'snapshots' | 'pointer' | 'filesBySnapshotId'>
): SourceFile[] | null => {
  const current = selectCurrentSnapshot(state);
  return current ? state.filesBySnapshotId[current.id] ?? null : null;
};

export const selectUndoTarget = (
  state: Pick<BlockVersionsState, 'snapshots' | 'pointer'>
): SnapshotRef | null =>
  state.pointer > 0 ? state.snapshots[state.pointer - 1] ?? null : null;

export const selectRedoTarget = (
  state: Pick<BlockVersionsState, 'snapshots' | 'pointer'>
): SnapshotRef | null =>
  state.pointer >= 0 ? state.snapshots[state.pointer + 1] ?? null : null;

export const selectIsBusy = (state: PanelState) =>
  selectChatIsBusy(state) || selectIsRestoring(state);

export const selectCanSend = (state: PanelState) =>
  selectChatCanSend(state) && !selectIsRestoring(state);

const canRestore = (state: PanelState) =>
  state.connectionState === 'ready' && !selectIsBusy(state);

export const selectCanUndo = (state: PanelState) =>
  canRestore(state) && selectUndoTarget(state) !== null;

export const selectCanRedo = (state: PanelState) =>
  canRestore(state) && selectRedoTarget(state) !== null;
